import React, { useEffect, useState } from 'react';
import { Box, Breadcrumbs, Button, Link, Skeleton, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormControlledTextField } from '../../common/Form/FormTextField';
import { FormControlledNumberField } from '../../common/Form/FormNumberField';
import ResourceSelector from '../../common/ResourceSelector';
import { FieldRequiredMark } from '../../common/Form/FieldRequiredMark';
import Loader from '../../common/Loader';
import MssAlert from '../../common/MssAlert';
import MssApiError from '../../common/MssApiError';
import { getData, postData } from '../../../DataAccessLayer';
import { flashcards, modules as modulesService } from '../../../DataAccessLayer/services';

function DeckGenerator() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const courseId = useSelector((state) => state.course.courseId);

  const [modulesList, setModulesList] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [isModulesError, setIsModulesError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: { title: '', cardCount: 20, resources: [] },
  });

  const loadModules = async () => {
    setIsLoadingModules(true);
    setIsModulesError(false);
    try {
      const data = await getData(modulesService(courseId));
      setModulesList(data);
    } catch (err) {
      console.error(err);
      setIsModulesError(true);
    } finally {
      setIsLoadingModules(false);
    }
  };

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const onSubmit = async ({ title, cardCount, resources }) => {
    setIsSubmitting(true);
    try {
      const selectedResourceNames = modulesList
        .filter((module) => resources.includes(module.id))
        .map((module) => module.name);

      const newDeck = await postData(flashcards(), {
        title: title.trim(),
        resources: selectedResourceNames,
        cardCount: Number(cardCount),
        courseId,
      });
      navigate(`${pathname}/${newDeck.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate flashcard deck. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="mss__centeredScreen mss__generator" id="deckGenerator">
      <Loader id="deckGenerator__loader_submit" isOpen={isSubmitting} />
      <Breadcrumbs className="mss__breadcrumbs" aria-label="breadcrumb">
        <Link component={RouterLink} to="..">
          myStudySidekick
        </Link>
        <Typography>Flashcard Generator</Typography>
      </Breadcrumbs>

      {isModulesError && (
        <MssAlert id="deckGenerator__alert_error" severity="error" type="statusAlert">
          <MssApiError
            id="deckGenerator__apiError"
            widgetName="Flashcard Generator"
            isPage
            reload={loadModules}
          />
        </MssAlert>
      )}

      {!isModulesError && (
        <>
          <Box className="generatorHeading">
            <Typography variant="h2" component="h1">
              Generate Flashcard Deck
            </Typography>
          </Box>

          <Box className="mss__box generatorForm" id="deckGenerator__box_form">
            <Box className="formSection">
              <Typography className="sectionLabel sectionLabel--accent">Title</Typography>

              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <FormControlledTextField
                  name="title"
                  control={control}
                  ariaLabel="Title"
                  placeholder="Module 2 Cards"
                  fullWidth
                />
              )}
            </Box>

            <Box className="formSection">
              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={160} />
              ) : (
                <Controller
                  name="resources"
                  control={control}
                  rules={{
                    validate: (value) => value.length > 0 || 'Select at least one resource',
                  }}
                  render={({ field }) => (
                    <ResourceSelector
                      id="deckGenerator"
                      modules={modulesList}
                      selected={field.value ?? []}
                      onChange={field.onChange}
                      helperText="Select resources from which the deck will be generated."
                      required
                    />
                  )}
                />
              )}
            </Box>

            <Box className="formSection">
              <Typography className="sectionLabel sectionLabel--accent">
                Specifications <FieldRequiredMark label="Number of cards" />
              </Typography>
              <Typography className="sectionHelper">Number of Cards in Deck (Limit 100)</Typography>
              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <FormControlledNumberField
                  name="cardCount"
                  control={control}
                  rules={{
                    required: 'Enter a number of flashcards to generate a new deck',
                    min: { value: 1, message: 'Number of flashcards must be greater than 0.' },
                    max: {
                      value: 100,
                      message: 'Total number of flashcards must be less than or equal to 100.',
                    },
                  }}
                  ariaLabel="Number of cards"
                  placeholder="Enter a number between 1-100"
                  htmlInputProps={{ min: 1, max: 100 }}
                  className="cardCountField"
                />
              )}
              <Box className="mss__formActions">
                <Button
                  variant="contained"
                  className="mss__button primary"
                  id="deckGenerator__button_generate"
                  disabled={!isValid || isSubmitting || isLoadingModules}
                  onClick={handleSubmit(onSubmit)}
                >
                  Generate Flashcard Deck
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default DeckGenerator;
